import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Amount } from '../../../../../common/components/Amount';
import { getValue } from '../../../../../common/global/helpers';
import Icons from '../../../../../common/widgets/Icons';
import Loader from '../../../../../common/widgets/Loader';
import { CustomerBudgetContext } from '../../../contexts/CustomerBudgetContext';
import { CustomerInfoContext } from '../../../contexts/CustomerInfoContext';
import { CardType, PlatformBenefitTypes } from '../../../enums';
import { resources } from '../../../resources/resources';
import WalletSkeleton from './WalletSkeleton';

interface WalletNavProps {
  toggleExpand?: () => void;
}

interface CardTypeConfig {
  className: string;
  resourceName: string;
}

const cardTypeLookup: Partial<Record<CardType, CardTypeConfig>> = {
  [CardType.Meal]: {
    className: 'dining',
    resourceName: resources.employeeCards.cardTypes.meal,
  },
  [CardType.Gift]: {
    className: 'gift',
    resourceName: resources.employeeCards.cardTypes.gift,
  },
  [CardType.Vacation]: {
    className: 'holiday',
    resourceName: resources.employeeCards.cardTypes.vacation,
  },
  [CardType.Culture]: {
    className: 'culture',
    resourceName: resources.employeeCards.cardTypes.culture,
  },
};

const valueSkeleton = <WalletSkeleton width={72} height={16} />;

export default function WalletNav({ toggleExpand }: Readonly<WalletNavProps>) {
  const { customerInfo } = useContext(CustomerInfoContext);
  const { customerBudget, isCustomerBalanceLoading } = useContext(
    CustomerBudgetContext,
  );
  const { t: translate } = useTranslation();

  const balance = customerBudget?.benefitBudgetBalance;
  const isBalanceLoading = isCustomerBalanceLoading || balance == null;
  const currentMonth = new Date().getMonth() + 1;
  const cards = customerBudget?.availableCards ?? [];
  const showCards =
    customerInfo.platformType === PlatformBenefitTypes.FlexibleBenefits && cards.length > 0;

  return (
    <div className="wallet">
      <div className="wallet-header">
        <button
          type="button"
          className="mobile-nav-back"
          onClick={toggleExpand}
          aria-label="Back"
        >
          <Icons.ArrowLeftMobile />
        </button>

        <Link to="/my-account/dashboard">
          {translate(resources.wallet.yourBudgetButton)}
        </Link>

        <div className="wallet-budget">
          <Loader
            loading={isBalanceLoading}
            loader={<WalletSkeleton width={150} height={34} />}
          >
            <Amount value={balance} />
          </Loader>
        </div>
      </div>

      {customerInfo.showWallet && (
        <Loader
          loading={isBalanceLoading}
          loader={
            <div className="wallet-cards-placeholder">
              <WalletSkeleton width="100%" height={150} />
            </div>
          }
        >
          {showCards ? (
            <div className="wallet-cards-list">
              {cards.map(card => {
                const cardConfig = cardTypeLookup[card.cardType];

                if (!cardConfig) {
                  return null;
                }

                return (
                  <Link
                    key={card.cardType}
                    className={`wallet-card card-${cardConfig.className}`}
                    to="/my-account/cards"
                  >
                    <div className="wallet-card-name">
                      {translate(cardConfig.resourceName)}
                    </div>

                    <div className="wallet-card-amount">
                      <span className="wallet-card-marker" />
                      <Amount
                        value={
                          card.isIntegrationAvailable ? card.balance : undefined
                        }
                      />
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : null}
        </Loader>
      )}

      <ul className="wallet-info">
        <li>
          <span className="wallet-info-label">
            {translate(resources.wallet.budgetAddedThisMonth)}{' '}
            {translate(getValue(resources.months, currentMonth.toString()))}
          </span>

          <span className="wallet-info-amount">
            <Loader loading={isBalanceLoading} loader={valueSkeleton}>
              <Amount value={customerBudget?.budgetAddedThisMonth} />
            </Loader>
          </span>
        </li>

        <li>
          <Loader
            loading={isBalanceLoading}
            loader={
              <div className="wallet-info-row-placeholder">
                <WalletSkeleton width={145} height={16} />
                {valueSkeleton}
              </div>
            }
          >
            {customerBudget?.budgetExpirationDate ? (
              <>
                <span className="wallet-info-label">
                  {translate(resources.wallet.budgetExpirationDate)}
                </span>
                <span className="wallet-info-amount">
                  {new Date(
                    customerBudget.budgetExpirationDate,
                  ).toLocaleDateString('ro-RO')}
                </span>
              </>
            ) : (
              <span className="wallet-info-label">
                {translate(resources.wallet.noBudgetExpirationDate)}
              </span>
            )}
          </Loader>
        </li>

        <li>
          <span className="wallet-info-label">
            {translate(resources.wallet.benefitBucks)}
          </span>

          <span className="wallet-info-amount">
            <Loader loading={isBalanceLoading} loader={valueSkeleton}>
              <Amount value={customerBudget?.benefitBucksBalance} />
            </Loader>
          </span>
        </li>
      </ul>
    </div>
  );
}
